// ─── İçerik Takip: status rules + column definitions ───────────────────────────
// Ported 1:1 from the PM's demo (pakodemy-content-tracking/app.js `derived`).
// Genel Done = at least one lesson video Status >= 3 AND at least 5 question videos
// approved (latest log Status >= 3). A step counts as done once the work has moved
// past it, e.g. a lesson video at Status 4/5 is also "Sisteme Hazır".

// Class options come from CMS/getsegments: one entry per ClassTypeId (= the
// classId the tracking API filters on, Segments."Class").
export const buildClassOptions = (segments) => {
  const seen = new Map();
  (segments || []).forEach((s) => {
    if (s.ClassTypeId != null && !seen.has(s.ClassTypeId)) {
      seen.set(s.ClassTypeId, { id: s.ClassTypeId, name: s.ClassTypeName });
    }
  });
  return [...seen.values()];
};

// ─── API → row ──────────────────────────────────────────────────────────────────
// The backend serves the two tabs separately (questiondetail / videodetail, flat
// rows). Genel Takip needs both sides, so they are merged by AchievementId into
// one row with nested `question` / `video` counts (the shape deriveRow reads).
// An achievement missing from one side gets zero counts there.
const identityOf = (r) => ({
  ClassName: r.ClassName,
  CourseId: r.CourseId,
  CourseName: r.CourseName,
  SegmentTopicName: r.SegmentTopicName,
  AchievementName: r.AchievementName,
  AchievementId: r.AchievementId,
});

const EMPTY_QUESTION = {
  QuestionCount: 0,
  VideoNoneCount: 0,
  DownloadedCount: 0,
  UploadedCount: 0,
  ApprovedCount: 0,
  ConvertedCount: 0,
};

const EMPTY_VIDEO = {
  VideoCount: 0,
  approvedInteractionVideoCount: 0,
  pendingVideoCount: 0,
  UploadedCount: 0,
  DoneCount: 0,
};

export const mergeTrackingRows = (questionRows, videoRows) => {
  const byId = new Map();
  const entry = (r) => {
    if (!byId.has(r.AchievementId)) {
      byId.set(r.AchievementId, { ...identityOf(r), question: EMPTY_QUESTION, video: EMPTY_VIDEO });
    }
    return byId.get(r.AchievementId);
  };
  (questionRows || []).forEach((r) => {
    entry(r).question = {
      QuestionCount: num(r.QuestionCount),
      VideoNoneCount: num(r.VideoNoneCount),
      DownloadedCount: num(r.DownloadedCount),
      UploadedCount: num(r.UploadedCount),
      ApprovedCount: num(r.ApprovedCount),
      ConvertedCount: num(r.ConvertedCount),
    };
  });
  (videoRows || []).forEach((r) => {
    entry(r).video = {
      VideoCount: num(r.VideoCount),
      approvedInteractionVideoCount: num(r.ApprovedInteractionVideoCount),
      pendingVideoCount: num(r.ReadyCount),
      UploadedCount: num(r.UploadedCount),
      DoneCount: num(r.DoneCount),
    };
  });
  return [...byId.values()];
};

// Course names can repeat inside one class (e.g. YKS: TYT + AYT Matematik), so the
// Ders filter works on CourseId and duplicate names get the id appended.
export const buildCourseLabels = (rows) => {
  const idsByName = new Map();
  rows.forEach((r) => {
    if (!idsByName.has(r.CourseName)) idsByName.set(r.CourseName, new Set());
    idsByName.get(r.CourseName).add(r.CourseId);
  });
  const labels = new Map();
  rows.forEach((r) => {
    const ambiguous = idsByName.get(r.CourseName).size > 1;
    labels.set(r.CourseId, ambiguous ? `${r.CourseName} (${r.CourseId})` : r.CourseName);
  });
  return labels;
};

// Question-video log status names (user feedback wording). Used by the Soru
// Takip columns and the drawer. Backend status: 0 / 1 / 2 / 3 / 4.
export const QUESTION_VIDEO_STATUS_LABEL = {
  VideoNoneCount: "Video panelinde yok",
  DownloadedCount: "Çözüm bekleniyor",
  UploadedCount: "Soru çözüldü",
  ApprovedCount: "Onaylandı",
  ConvertedCount: "Tamamlandı",
};

export const Stage = { Done: "done", Progress: "progress", Missing: "missing" };

export const STAGE_LABEL = {
  [Stage.Done]: "Tamamlandı",
  [Stage.Progress]: "Devam ediyor",
  [Stage.Missing]: "Eksik",
};

const STAGE_RANK = { [Stage.Missing]: 0, [Stage.Progress]: 1, [Stage.Done]: 2 };

const stageState = (done, partial) =>
  done ? Stage.Done : partial ? Stage.Progress : Stage.Missing;

const num = (v) => Number(v || 0);

export const deriveRow = (row) => {
  const q = row.question || {};
  const v = row.video || {};
  const recordedQuestionVideos =
    num(q.DownloadedCount) + num(q.UploadedCount) + num(q.ApprovedCount) + num(q.ConvertedCount);
  const approvedQuestionVideos = num(q.ApprovedCount) + num(q.ConvertedCount);
  const videoCount = num(v.VideoCount);
  const interactionCount = num(v.approvedInteractionVideoCount);
  const questionCount = num(q.QuestionCount);

  const lessonVideoUploaded = videoCount > 0;
  const interactionApproved = videoCount > 0 && interactionCount >= videoCount;
  const systemReady = num(v.pendingVideoCount) + num(v.UploadedCount) + num(v.DoneCount) > 0;
  const fiveQuestions = questionCount >= 5;
  const questionVideosRecorded = recordedQuestionVideos >= 5;
  const questionsApproved = approvedQuestionVideos >= 5;

  const flags = [
    lessonVideoUploaded,
    interactionApproved,
    systemReady,
    fiveQuestions,
    questionVideosRecorded,
    questionsApproved,
  ];

  const progress = Math.round((flags.filter(Boolean).length / flags.length) * 100);
  const systemReadyOrLater = num(v.pendingVideoCount) + num(v.UploadedCount) + num(v.DoneCount);

  return {
    recordedQuestionVideos,
    approvedQuestionVideos,
    systemReady,
    questionsApproved,
    overallDone: systemReady && questionsApproved,
    progress,
    incomplete: progress < 100,
    // Videos whose interaction is approved but that aren't Status >= 3 yet. The API
    // gives counts, not per-video rows, so this assumes every Status >= 3 video
    // already has its interaction approved (the pipeline order).
    videosAwaitingSystem: Math.max(0, interactionCount - systemReadyOrLater),
    // Lesson video uploaded but the 5 questions aren't in yet.
    awaitingQuestions: lessonVideoUploaded && !fiveQuestions,
    stages: {
      lessonVideo: stageState(lessonVideoUploaded),
      interaction: stageState(interactionApproved, interactionCount > 0),
      systemReady: stageState(systemReady),
      fiveQuestions: stageState(fiveQuestions, questionCount > 0),
      questionVideos: stageState(questionVideosRecorded, recordedQuestionVideos > 0),
      questionsApproved: stageState(questionsApproved, approvedQuestionVideos > 0),
      // "Question Status" pill on the Soru Takip tab: >4 questions = Completed.
      questionStatus: questionCount > 4 ? Stage.Done : questionCount > 0 ? Stage.Progress : Stage.Missing,
    },
  };
};

// Status of a raw count cell. goal → done at goal; invert → done at 0.
export const countStage = (value, goal, invert) => {
  const n = num(value);
  if (invert) return n === 0 ? Stage.Done : Stage.Progress;
  if (goal != null) return n >= goal ? Stage.Done : n > 0 ? Stage.Progress : Stage.Missing;
  return n > 0 ? Stage.Done : Stage.Missing;
};

// ─── Columns ────────────────────────────────────────────────────────────────────
// Every column exposes:
//   value(row)       → primitive used by the header filter (options + matching)
//   optionLabel(row) → text shown for that value in the filter (defaults to value)
//   sort(row)        → primitive used for sorting (defaults to value)
// `type` drives cell rendering in TrackingTable; `group` drives the grouped header
// and the separators (only the Genel Takip tab sets groups).

const textCol = (key, label, extra) => ({ key, label, type: "text", value: (r) => r[key], ...extra });

const stageCol = (key, label, group) => ({
  key,
  label,
  group,
  type: "stage",
  align: "center",
  width: 82,
  stage: (r) => r._d.stages[key],
  value: (r) => STAGE_LABEL[r._d.stages[key]],
  sort: (r) => STAGE_RANK[r._d.stages[key]],
});

const countCol = (key, label, pick, opts = {}) => ({
  key,
  label,
  type: "count",
  pick,
  goal: opts.goal,
  invert: opts.invert,
  value: (r) => num(pick(r)),
});

// No Sınıf column: the class is already picked at the top of the page.
// They are `frozen` on every tab (pinned left, need a fixed width each).
const identityColumns = (group) => [
  textCol("CourseId", "Ders", {
    group,
    strong: true,
    frozen: true,
    width: 110,
    text: (r) => r.CourseName,
    optionLabel: (r) => r._courseLabel,
    sort: (r) => r._courseLabel,
  }),
  textCol("SegmentTopicName", "Konu", { group, frozen: true, width: 130 }),
  textCol("AchievementName", "Kazanım", { group, type: "achievement", frozen: true, width: 220 }),
  textCol("AchievementId", "AchievementId", { group, type: "id", frozen: true, width: 132 }),
];

export const TABS = [
  {
    id: "general",
    label: "Genel Takip",
    columns: [
      ...identityColumns("Kazanım"),
      stageCol("lessonVideo", "Video Yüklendi", "Ders Videosu"),
      stageCol("interaction", "Etkileşim Onaylandı", "Ders Videosu"),
      stageCol("systemReady", "Sisteme Hazır", "Ders Videosu"),
      stageCol("fiveQuestions", "5 Soru", "Soru"),
      stageCol("questionVideos", "Soru Videoları", "Soru"),
      stageCol("questionsApproved", "Onaylandı", "Soru"),
      {
        key: "overall",
        label: "Genel Status",
        group: "Genel Durum",
        type: "overall",
        value: (r) => (r._d.overallDone ? "Done" : "In Progress"),
      },
      {
        key: "progress",
        label: "Progress",
        group: "Genel Durum",
        type: "progress",
        value: (r) => `%${r._d.progress}`,
        sort: (r) => r._d.progress,
      },
    ],
  },
  {
    id: "questions",
    label: "Soru Takip",
    columns: [
      ...identityColumns(),
      countCol("QuestionCount", "Soru", (r) => r.question.QuestionCount, { goal: 5 }),
      {
        key: "questionStatus",
        label: "Question Status",
        type: "pill",
        stage: (r) => r._d.stages.questionStatus,
        value: (r) => (r._d.stages.questionStatus === Stage.Done ? "Completed" : "Missing"),
      },
      countCol("VideoNoneCount", QUESTION_VIDEO_STATUS_LABEL.VideoNoneCount, (r) => r.question.VideoNoneCount, { invert: true }),
      countCol("DownloadedCount", QUESTION_VIDEO_STATUS_LABEL.DownloadedCount, (r) => r.question.DownloadedCount),
      countCol("QUploadedCount", QUESTION_VIDEO_STATUS_LABEL.UploadedCount, (r) => r.question.UploadedCount),
      countCol("ApprovedCount", QUESTION_VIDEO_STATUS_LABEL.ApprovedCount, (r) => r.question.ApprovedCount),
      countCol("ConvertedCount", QUESTION_VIDEO_STATUS_LABEL.ConvertedCount, (r) => r.question.ConvertedCount),
    ],
  },
  {
    id: "videos",
    label: "Ders Videosu",
    columns: [
      ...identityColumns(),
      countCol("VideoCount", "Video", (r) => r.video.VideoCount),
      countCol("approvedInteractionVideoCount", "Etkileşim Onaylı", (r) => r.video.approvedInteractionVideoCount),
      countCol("pendingVideoCount", "Status 3 · Hazır", (r) => r.video.pendingVideoCount),
      countCol("VUploadedCount", "Status 4 · Uploaded", (r) => r.video.UploadedCount),
      countCol("DoneCount", "Status 5 · Done", (r) => r.video.DoneCount),
    ],
  },
];

// Identity columns are shared by every tab, so their filters survive a tab switch.
export const IDENTITY_KEYS = identityColumns().map((c) => c.key);

export const columnOptionLabel = (col, row) =>
  String(col.optionLabel ? col.optionLabel(row) : col.value(row));

export const columnSortValue = (col, row) => (col.sort ? col.sort(row) : col.value(row));

// Filter value semantics (same as AchievementReactions/DropdownFilter):
//   null / missing → no filter, [] → nothing passes, [...] → only those values.
export const applyColumnFilters = (rows, columns, filters, skipKey) =>
  rows.filter((row) =>
    columns.every((col) => {
      if (col.key === skipKey) return true;
      const selected = filters[col.key];
      if (selected == null) return true;
      return selected.includes(col.value(row));
    })
  );

export const matchesSearch = (row, term) => {
  if (!term) return true;
  const haystack = `${row.CourseName} ${row.SegmentTopicName} ${row.AchievementName} ${row.AchievementId}`;
  return haystack.toLocaleLowerCase("tr-TR").includes(term);
};
